<div class="om-page">
  <app-project-nav></app-project-nav>
  <a class="om-back" data-testid="om-back" [routerLink]="overviewLink">
    <mat-icon aria-hidden="true">chevron_left</mat-icon>
    {{ 'objectMappings.detail.back' | translate }}
  </a>

  @switch (pageState()) {
    @case ('notFound') {
      <div class="om-hint om-hint--error" role="alert" data-testid="om-not-found">
        <p>{{ 'objectMappings.detail.notFound' | translate }}</p>
        <a mat-stroked-button data-testid="om-not-found-back" [routerLink]="overviewLink">
          {{ 'objectMappings.detail.toOverview' | translate }}
        </a>
      </div>
    }
    @case ('error') {
      <div class="om-hint om-hint--error" role="alert" data-testid="om-page-error">
        <p>{{ 'objectMappings.detail.headError' | translate }}</p>
        <button mat-stroked-button type="button" data-testid="om-page-retry" (click)="retryPage()">
          {{ 'objectMappings.retry' | translate }}
        </button>
      </div>
    }
    @default {
      <ng-container *ngTemplateOutlet="page" />
    }
  }
</div>

<ng-template #page>
  <header class="om-head" data-testid="om-detail-head">
    <div class="om-head__main">
      <div class="om-head__title">
        <app-icon class="om-head__icon" name="library" [size]="20" />
        @if (library(); as lib) {
          <h1 class="om-head__name" data-testid="om-head-name">{{ lib.name }}</h1>
        } @else if (headState() === 'loading') {
          <span class="om-skeleton om-skeleton--title" aria-hidden="true"></span>
        } @else {
          <h1 class="om-head__name">{{ 'objectMappings.detail.titleFallback' | translate }}</h1>
        }
      </div>
      @if (library(); as lib) {
        <div class="om-head__meta">
          <span class="om-tech" data-testid="om-head-tech">
            @if (technologyOf(lib.engineId); as techKey) {
              {{ techKey | translate }}
            } @else {
              {{ lib.engineId }}
            }
          </span>
          @if (lib.address) {
            <code class="om-head__address" data-testid="om-head-address" [matTooltip]="lib.address">
              {{ lib.address }}
            </code>
          }
          <span class="om-head__count" data-testid="om-head-count">
            ·
            {{
              (lib.entryCount === 1 ? 'objectMappings.detail.objectCountOne' : 'objectMappings.detail.objectCount')
                | translate: { count: lib.entryCount }
            }}
          </span>
        </div>
      }
      @if (library() && (!isReadOnly() || tags().length > 0)) {
        <div class="om-tags" data-testid="om-tags">
          <span class="om-tags__label">{{ 'objectMappings.detail.tags.label' | translate }}</span>
          @for (tag of tags(); track tag) {
            <span class="om-tags__chip" data-testid="om-tag">
              <span class="om-tags__text">{{ tag }}</span>
              @if (!isReadOnly()) {
                <button
                  type="button"
                  class="om-tags__remove"
                  data-testid="om-tag-remove"
                  [attr.aria-label]="'objectMappings.detail.tags.remove' | translate: { tag }"
                  (click)="removeTag(tag)"
                >
                  <mat-icon aria-hidden="true">close</mat-icon>
                </button>
              }
            </span>
          }
          @if (!isReadOnly()) {
            <input
              #tagInput
              class="om-tags__input"
              data-testid="om-tag-input"
              [attr.maxlength]="fieldLengths.NAME_MAX"
              [placeholder]="'objectMappings.detail.tags.add' | translate"
              [attr.aria-label]="'objectMappings.detail.tags.addLabel' | translate"
              (keydown.enter)="addTag(tagInput)"
            />
          }
        </div>
      }
      @if (tagError(); as message) {
        <p class="om-tags-error" role="alert" data-testid="om-tags-error">{{ message }}</p>
      }
    </div>
    <div class="om-head__actions" data-testid="om-head-actions">
      @if (!isReadOnly()) {
        <button
          mat-stroked-button
          type="button"
          class="om-library-delete"
          data-testid="om-library-delete"
          [disabled]="!library()"
          (click)="deleteLibrary()"
        >
          <mat-icon aria-hidden="true">delete</mat-icon>
          {{ 'objectMappings.detail.actions.deleteLibrary' | translate }}
        </button>
      }
      <button
        mat-stroked-button
        type="button"
        data-testid="om-library-usages"
        [disabled]="!library()"
        (click)="showUsages()"
      >
        <mat-icon aria-hidden="true">link</mat-icon>
        {{ 'objectMappings.detail.actions.usagesLibrary' | translate }}
      </button>
      @if (!isReadOnly()) {
        <button
          mat-stroked-button
          type="button"
          data-testid="om-rename-library"
          [disabled]="!library()"
          (click)="openRename()"
        >
          <mat-icon aria-hidden="true">edit</mat-icon>
          {{ 'objectMappings.detail.actions.renameLibrary' | translate }}
        </button>
        @if (recordable()) {
          <button mat-stroked-button type="button" data-testid="om-record" (click)="recordDialogOpen.set(true)">
            <span class="om-rec-dot" aria-hidden="true"></span>
            {{ 'objectMappings.detail.actions.record' | translate }}
          </button>
        }
        <button
          mat-flat-button
          type="button"
          data-testid="om-entry-add"
          [disabled]="!library()"
          (click)="openEntryDialog()"
        >
          <mat-icon aria-hidden="true">add</mat-icon>
          {{ 'objectMappings.detail.actions.addEntry' | translate }}
        </button>
      }
    </div>
  </header>

  @if (pendingCaptures() > 0) {
    <p class="om-review-pending" role="status" data-testid="om-review-pending">
      <mat-icon aria-hidden="true">fact_check</mat-icon>
      <span>{{ pendingCapturesKey() | translate: { count: pendingCaptures() } }}</span>
      <a mat-stroked-button [routerLink]="reviewLink">{{ 'objectMappings.detail.pendingCapturesLink' | translate }}</a>
    </p>
  }

  @if (notice(); as message) {
    <p class="om-hint om-hint--notice" role="status" data-testid="om-notice">{{ message }}</p>
  }

  <div
    class="om-view-switch"
    role="tablist"
    data-testid="om-view-switch"
    [attr.aria-label]="'objectMappings.detail.view.label' | translate"
  >
    @for (tab of viewTabs(); track tab.id) {
      <button
        type="button"
        role="tab"
        class="om-view-tab"
        aria-controls="om-view-panel"
        [id]="'om-view-tab-' + tab.id"
        [class.om-view-tab--active]="tab.active"
        [attr.data-testid]="'om-view-' + tab.id"
        [attr.aria-selected]="tab.active"
        [attr.tabindex]="tab.active ? 0 : -1"
        (click)="selectView(tab.id)"
        (keydown)="onViewKeydown($event)"
      >
        {{ tab.label | translate }}
        @if (tab.count !== null) {
          <span class="om-view-tab__count">{{ tab.count }}</span>
        }
      </button>
    }
  </div>

  <div class="om-view-panel" id="om-view-panel" role="tabpanel" [attr.aria-labelledby]="'om-view-tab-' + view()">
    @if (view() === 'zuordnung') {
      <app-object-assignment-view
        [projectId]="projectId"
        [libraryName]="library()?.name ?? ''"
        [rows]="assignmentRows()"
        [openRows]="openRows()"
        [status]="assignmentStatus()"
        [readOnly]="isReadOnly()"
        (retry)="retryAssignment()"
        (createObject)="createFromReference($event)"
      />
    } @else {
      <div class="om-toolbar">
        <app-search-bar
          class="om-search"
          data-testid="om-entry-search"
          [placeholder]="'objectMappings.detail.searchPlaceholder' | translate"
          [value]="searchTerm()"
          (valueChange)="onSearchChange($event)"
        />
        <div
          class="om-state-filter"
          role="group"
          data-testid="om-state-filter"
          [attr.aria-label]="'objectMappings.state.filterLabel' | translate"
        >
          @for (chip of stateChips(); track chip.key) {
            <app-filter-chip
              [attr.data-testid]="'om-chip-' + chip.key"
              [label]="'objectMappings.state.chip.' + chip.key | translate"
              [count]="chip.count"
              [pill]="true"
              [active]="chip.active"
              (clicked)="selectState(chip.state)"
            />
          }
        </div>
        @if (!loading() && state() === 'ok') {
          <span class="om-count" role="status">
            {{ 'objectMappings.detail.shownCount' | translate: { shown: rows().length, total: total() } }}
          </span>
        }
      </div>

      <div class="om-card" role="table" [attr.aria-label]="'objectMappings.detail.tableLabel' | translate">
        <div class="om-table-header" role="row">
          <div class="om-col om-col--toggle" role="columnheader"></div>
          <div class="om-col om-col--name" role="columnheader">
            {{ 'objectMappings.detail.table.name' | translate }}
          </div>
          <div class="om-col om-col--type" role="columnheader">
            {{ 'objectMappings.detail.table.type' | translate }}
          </div>
          <div class="om-col om-col--best" role="columnheader">
            {{ 'objectMappings.detail.table.bestCandidate' | translate }}
          </div>
          <div class="om-col om-col--last-hit" role="columnheader">
            {{ 'objectMappings.state.lastHit' | translate }}
          </div>
          <div class="om-col om-col--actions" role="columnheader">
            {{ 'objectMappings.detail.table.actions' | translate }}
          </div>
        </div>

        @if (loading()) {
          <div class="om-row--state" role="row">
            <div class="om-col om-col--empty" role="cell">
              <div
                class="om-skeleton-body"
                role="status"
                data-testid="om-loading"
                [attr.aria-label]="'objectMappings.detail.loading' | translate"
              >
                @for (line of skeletonRows; track line) {
                  <div class="om-row om-skeleton-row" aria-hidden="true">
                    <span class="om-skeleton om-skeleton--name"></span>
                    <span class="om-skeleton om-skeleton--type"></span>
                    <span class="om-skeleton om-skeleton--best"></span>
                  </div>
                }
              </div>
            </div>
          </div>
        } @else if (state() === 'error') {
          <div class="om-row om-row--empty" role="row">
            <div class="om-col om-col--empty" role="cell">
              <div class="om-hint om-hint--error" role="alert" data-testid="om-error">
                <p>{{ 'objectMappings.detail.loadError' | translate }}</p>
                <button mat-stroked-button type="button" data-testid="om-retry" (click)="retry()">
                  {{ 'objectMappings.retry' | translate }}
                </button>
              </div>
            </div>
          </div>
        } @else if (rows().length === 0) {
          <div class="om-row om-row--empty" role="row">
            <div class="om-col om-col--empty" role="cell">
              @if (library()?.entryCount !== 0 && (entryState() || searchTerm().trim())) {
                <p class="om-hint" data-testid="om-empty-filtered">
                  {{ 'objectMappings.detail.emptyFiltered' | translate }}
                </p>
              } @else {
                <p class="om-hint" data-testid="om-empty">{{ 'objectMappings.detail.empty' | translate }}</p>
              }
            </div>
          </div>
        } @else {
          @let libName = library()?.name ?? '';
          @for (row of rows(); track row.id) {
            <div class="om-row om-row--entry" role="row" data-testid="om-entry" tabindex="0">
              <div class="om-col om-col--toggle" role="cell">
                @if (row.candidates.length > 0) {
                  <button
                    type="button"
                    class="om-toggle"
                    data-testid="om-toggle"
                    [class.om-toggle--open]="isExpanded(row.id)"
                    [attr.aria-expanded]="isExpanded(row.id)"
                    [attr.aria-controls]="'om-candidates-' + row.id"
                    [attr.aria-label]="'objectMappings.detail.expand' | translate"
                    (click)="toggle(row.id)"
                  >
                    <mat-icon aria-hidden="true">chevron_right</mat-icon>
                  </button>
                }
              </div>
              <div class="om-col om-col--name" role="cell">
                <code
                  class="om-ref"
                  [matTooltip]="'objectMappings.detail.inStep' | translate: { ref: libName + '.' + row.logicalName }"
                >
                  @if (libName) {
                    <span class="om-ref__lib">{{ libName }}.</span>
                  }
                  <span class="om-ref__name" data-testid="om-name">{{ row.logicalName }}</span>
                </code>
                @if (row.area; as area) {
                  <span class="om-area" data-testid="om-area">{{ area }}</span>
                }
                @if (row.missingSince; as since) {
                  <app-status-pill
                    data-testid="om-missing"
                    kind="inaktiv"
                    size="sm"
                    [label]="'objectMappings.state.missingSince' | translate: { date: since }"
                  />
                }
              </div>
              <div class="om-col om-col--type" role="cell">{{ row.typeName }}</div>
              <div class="om-col om-col--best" role="cell" data-testid="om-best">
                @if (row.candidates[0]; as best) {
                  <span class="om-kind">{{ best.label | translate }}</span>
                  <code class="om-locator">{{ best.value }}</code>
                } @else {
                  <span class="om-none">{{ 'objectMappings.detail.noCandidates' | translate }}</span>
                }
              </div>
              <div class="om-col om-col--last-hit" role="cell" data-testid="om-last-hit">
                @if (row.lastHit; as hit) {
                  @if (hit.warning) {
                    @let tip = hit.warning | translate;
                    @let hitText = 'objectMappings.state.hitFragile' | translate: { when: hit.when };
                    <span
                      class="om-last-hit om-last-hit--warn"
                      tabindex="0"
                      [matTooltip]="tip"
                      [attr.aria-label]="hitText + ': ' + tip"
                    >
                      {{ hitText }}
                    </span>
                  } @else {
                    <span class="om-last-hit">{{ hit.when }}</span>
                  }
                } @else {
                  <span class="om-none">{{ 'objectMappings.state.never' | translate }}</span>
                }
              </div>
              <div class="om-col om-col--actions" role="cell">
                <button
                  type="button"
                  class="om-action"
                  data-testid="om-usages"
                  [attr.aria-label]="'objectMappings.detail.actions.usages' | translate: { name: row.logicalName }"
                  (click)="showUsages(row)"
                >
                  <mat-icon aria-hidden="true">link</mat-icon>
                </button>
                @if (!isReadOnly()) {
                  <button
                    type="button"
                    class="om-action"
                    data-testid="om-entry-edit"
                    [disabled]="!library()"
                    [attr.aria-label]="'objectMappings.detail.actions.edit' | translate: { name: row.logicalName }"
                    (click)="openEntryDialog(row)"
                  >
                    <mat-icon aria-hidden="true">edit</mat-icon>
                  </button>
                  <button
                    type="button"
                    class="om-action"
                    data-testid="om-rename"
                    [attr.aria-label]="'objectMappings.detail.actions.rename' | translate: { name: row.logicalName }"
                    (click)="openRename(row)"
                  >
                    <mat-icon aria-hidden="true">drive_file_rename_outline</mat-icon>
                  </button>
                  <button
                    type="button"
                    class="om-action om-action--danger"
                    data-testid="om-delete"
                    [attr.aria-label]="'objectMappings.detail.actions.delete' | translate: { name: row.logicalName }"
                    (click)="deleteEntry(row)"
                  >
                    <mat-icon aria-hidden="true">delete</mat-icon>
                  </button>
                }
              </div>
            </div>
            @if (isExpanded(row.id)) {
              <div class="om-row om-row--candidates" role="row">
                <div class="om-col om-col--candidates" role="cell">
                  <div class="om-candidates-head" data-testid="om-candidates-head">
                    {{ 'objectMappings.detail.candidatesHead' | translate }}
                    <span class="om-candidates-head__count">· {{ row.candidates.length }}</span>
                  </div>
                  <ol
                    class="om-candidates"
                    data-testid="om-candidates"
                    tabindex="0"
                    [id]="'om-candidates-' + row.id"
                    [attr.aria-label]="
                      'objectMappings.detail.candidatesLabel' | translate: { count: row.candidates.length }
                    "
                  >
                    @for (candidate of row.candidates; track $index) {
                      <li class="om-candidate">
                        <span class="om-rank">{{ $index + 1 }}</span>
                        <span class="om-kind">{{ candidate.label | translate }}</span>
                        <code class="om-locator">{{ candidate.value }}</code>
                        @if (candidate.extras) {
                          <span class="om-extras">{{ candidate.extras }}</span>
                        }
                      </li>
                    }
                  </ol>
                </div>
              </div>
            }
          }
        }
      </div>

      @if (!loading() && state() === 'ok') {
        @if (moreError()) {
          <p class="om-hint om-hint--error" role="alert" data-testid="om-more-error">
            {{ 'objectMappings.detail.loadMoreError' | translate }}
          </p>
        }
        @if (canLoadMore()) {
          <div class="om-more">
            <button
              mat-stroked-button
              type="button"
              data-testid="om-load-more"
              [disabled]="loadingMore()"
              [attr.aria-busy]="loadingMore()"
              (click)="loadMore()"
            >
              {{ (loadingMore() ? 'objectMappings.detail.loadingMore' : 'objectMappings.detail.loadMore') | translate }}
            </button>
          </div>
        }
        @let syntaxLib = library()?.name ?? '';
        <div class="om-syntax" data-testid="om-syntax">
          <span>
            {{ 'objectMappings.detail.syntax.step' | translate }}
            <code>
              {{ 'objectMappings.detail.syntax.stepCode' | translate: { ref: syntaxLib + '.' + firstObjectName() } }}
            </code>
          </span>
          <span>
            {{ 'objectMappings.detail.syntax.condition' | translate }}
            <code>{{ 'objectMappings.detail.syntax.conditionCode' | translate: { lib: syntaxLib } }}</code>
          </span>
        </div>
        <p class="om-footnote" data-testid="om-never-hit-hint">{{ 'objectMappings.state.neverHitHint' | translate }}</p>
      }
    }
  </div>

  <app-verwendung-card
    testId="om-usage-card"
    [subtitle]="'objectMappings.detail.usage.subtitle' | translate"
    [tabs]="usageTabs()"
    [status]="usageStatus()"
    (erneutVersuchen)="loadUsage()"
  />
</ng-template>

@if (renameTarget(); as target) {
  <app-object-rename-dialog
    [projectId]="projectId"
    [mappingId]="mappingId"
    [entryId]="target.entryId"
    [currentName]="target.name"
    (renamed)="onRenamed($event)"
    (cancelled)="renameTarget.set(null)"
  />
}

@if (recordDialogOpen() && library(); as current) {
  <app-object-mapping-create-dialog
    [projectId]="projectId"
    [library]="current"
    (cancelled)="recordDialogOpen.set(false)"
  />
}

@if (entryDialog(); as target) {
  <app-object-entry-dialog
    [projectId]="projectId"
    [mappingId]="mappingId"
    [entry]="target.entry"
    [presetName]="target.presetName ?? ''"
    [libraryName]="library()?.name ?? ''"
    [engineId]="library()?.engineId ?? null"
    (saved)="onEntrySaved($event)"
    (cancelled)="entryDialog.set(null)"
  />
}

@if (usagesView(); as view) {
  <app-confirm-dialog
    [visible]="true"
    [maxWidthRem]="40"
    icon="link"
    iconTon="akzent"
    confirmColor="primary"
    [showCancel]="false"
    [title]="'objectMappings.detail.usagesTitle' | translate: { name: view.name }"
    [message]="
      (view.entryId ? 'objectMappings.detail.usagesMessage' : 'objectMappings.detail.usagesLibraryMessage') | translate
    "
    [confirmLabel]="'objectMappings.detail.close' | translate"
    [cancelLabel]="'objectMappings.detail.close' | translate"
    (confirmed)="usagesView.set(null)"
    (cancelled)="usagesView.set(null)"
  >
    @if (view.error) {
      <p class="om-hint om-hint--error" role="alert" data-testid="om-usages-error">
        {{ 'objectMappings.detail.usagesError' | translate }}
      </p>
    } @else if (view.usages; as usages) {
      <app-object-usage-list
        class="om-usages"
        [usages]="usages"
        [projectId]="projectId"
        (opened)="usagesView.set(null)"
      />
    } @else {
      <div class="om-loading om-loading--inline" data-testid="om-usages-loading" role="status">
        <mat-spinner diameter="24" />
        <span>{{ 'objectMappings.detail.usagesLoading' | translate }}</span>
      </div>
    }
  </app-confirm-dialog>
}

@if (deleteView(); as view) {
  <app-confirm-dialog
    [visible]="true"
    [maxWidthRem]="35"
    icon="delete"
    [title]="'objectMappings.detail.deleteTitle' | translate: { name: view.row.logicalName }"
    [message]="
      view.loading
        ? ''
        : view.usedBy
          ? ('objectMappings.detail.deleteUsedBy' | translate: { ids: view.usedBy })
          : ('objectMappings.detail.deleteQuestion' | translate)
    "
    [confirmLabel]="(view.usedBy ? 'objectMappings.detail.deleteConfirm' : 'objectMappings.detail.delete') | translate"
    [cancelLabel]="'objectMappings.rename.cancel' | translate"
    [busy]="view.busy"
    [confirmDisabled]="view.loading"
    (confirmed)="confirmDelete()"
    (cancelled)="deleteView.set(null)"
  >
    @if (view.loading) {
      <div class="om-loading om-loading--inline" data-testid="om-delete-loading" role="status">
        <mat-spinner diameter="24" />
        <span>{{ 'objectMappings.detail.usagesLoading' | translate }}</span>
      </div>
    }
    @if (view.error) {
      <p class="om-hint om-hint--error" role="alert" data-testid="om-delete-error">{{ view.error }}</p>
    }
  </app-confirm-dialog>
}

@if (libraryDeleteView(); as view) {
  <app-confirm-dialog
    [visible]="true"
    [maxWidthRem]="35"
    icon="delete"
    [title]="'objectMappings.detail.deleteLibrary.title' | translate: { name: library()?.name ?? mappingId }"
    [message]="
      view.loading
        ? ''
        : view.usedBy
          ? ('objectMappings.detail.deleteLibrary.usedBy' | translate: { ids: view.usedBy })
          : ('objectMappings.detail.deleteLibrary.question' | translate)
    "
    [confirmLabel]="
      (view.usedBy ? 'objectMappings.detail.deleteLibrary.confirmAnyway' : 'objectMappings.detail.delete') | translate
    "
    [cancelLabel]="'objectMappings.rename.cancel' | translate"
    [busy]="view.busy"
    [confirmDisabled]="view.loading"
    (confirmed)="confirmLibraryDelete()"
    (cancelled)="libraryDeleteView.set(null)"
  >
    @if (view.loading) {
      <div class="om-loading om-loading--inline" data-testid="om-library-delete-loading" role="status">
        <mat-spinner diameter="24" />
        <span>{{ 'objectMappings.detail.usagesLoading' | translate }}</span>
      </div>
    }
    @if (view.error) {
      <p class="om-hint om-hint--error" role="alert" data-testid="om-library-delete-error">{{ view.error }}</p>
    }
  </app-confirm-dialog>
}
